import {
	queryOptions,
	useMutation,
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import type { Source, VocabularyContent } from "~/lib/server/db/schema/flashcard-schema";
import { getVocabularyItem } from "../server/functions/learn";
import {
	type ExampleSentence,
	generateExampleSentenceFromMassif,
	generateExampleSentenceUsingAi,
} from "../server/functions/vocab";

type GenerateAiExampleVariables = {
	data: {
		word: string;
		meaning: { meaning: string; example: string };
		readings: string[];
		level: string;
		vocabId: string;
	};
};

export const vocabularyQueryKeys = {
	all: ["vocabulary"] as const,
	detail: (id: string) => [...vocabularyQueryKeys.all, "detail", id] as const,
	examples: (id: string) =>
		[...vocabularyQueryKeys.detail(id), "examples"] as const,
};

export const vocabularyItemQueryOptions = (vocabId: string) =>
	queryOptions({
		queryKey: vocabularyQueryKeys.detail(vocabId),
		queryFn: () => getVocabularyItem({ data: { vocabId } }),
		enabled: !!vocabId,
	});

export const vocabularyExamplesQueryOptions = (vocab: Source | null) =>
	queryOptions({
		queryKey: vocabularyQueryKeys.examples(vocab?.sid ?? ""),
		queryFn: async (): Promise<ExampleSentence[]> => {
			if (!vocab) return [];

			if (vocab.type === "vocabulary") {
				const vocabularyContent = vocab.content as VocabularyContent;
				if (vocabularyContent.phrases && vocabularyContent.phrases.length > 0) {
					return vocabularyContent.phrases.map(
						(p): ExampleSentence => ({
							sentence: p.phrase,
							source: "https://github.com/Ronokof/Full-Japanese-Study-Deck", // TODO: need to figure out what to call this
							translation: p.translation,
						}),
					);
				}
			}

			const result = await generateExampleSentenceFromMassif({
				data: {
					word: vocab.reading,
					count: 3,
				},
			});

			if ("error" in result) {
				console.error("Error fetching massif examples:", result.error);
				return [];
			}

			return result;
		},
	});

export const useVocabularyItem = (vocabId: string) =>
	useSuspenseQuery(vocabularyItemQueryOptions(vocabId));

export const useVocabularySentenceExamples = (vocab: Source | null) =>
	useSuspenseQuery(vocabularyExamplesQueryOptions(vocab));

export const useGenerateAIExample = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (vars: GenerateAiExampleVariables) =>
			generateExampleSentenceUsingAi({ data: vars.data }),
		onSuccess: (data, variables) => {
			if (!("error" in data)) {
				queryClient.invalidateQueries({
					queryKey: vocabularyQueryKeys.examples(variables.data.vocabId),
				});
			}
		},
	});
};

type GenerateMassifExampleVariables = {
	data: {
		word: string;
		count: number;
		vocabId: string;
	};
};

export const useGenerateMassifExample = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (vars: GenerateMassifExampleVariables) =>
			generateExampleSentenceFromMassif({
				data: { word: vars.data.word, count: vars.data.count },
			}),
		onSuccess: (data, variables) => {
			if (!("error" in data)) {
				queryClient.invalidateQueries({
					queryKey: vocabularyQueryKeys.examples(variables.data.vocabId),
				});
			}
		},
	});
};
